import type { Dispatch, ReactElement } from "react"
import { useRef, useState } from "react"

import { useTranslation } from "react-i18next"
import { useMutation } from "react-query"
import { useParams } from "react-router-dom"

import { NotificationUtils } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"

import { KonanAPI } from "../../services/KonanAPI"
import type { ActualDecisionCreationJob } from "../../types/generated/api/ActualDecisionCreationJob"
import { DataFileGroup } from "../../types/generated/api/DataFileGroup"
import { type StandardErrorType, withErrorHandler } from "../../utils/ErrorHandling"
import { readFileContentAsync } from "../../utils/genericHelpers"
import { Dropzone } from "../form/Dropzone"
import { BaseSimpleDialog } from "./BaseSimpleDialog"

/**
 * ActualDecisionsDialog component handles the uploading and processing of actual decision files.
 *
 * @param {Object} props - Component properties.
 * @param {Function} props.onClose - Callback function to close the dialog.
 * @param {React.MutableRefObject<boolean>} props.isActualDecisionFileQueryRunningRef - Reference to track if a query is running for actual decision files.
 *
 * @returns {ReactElement} - React element representing the ActualDecisionsDialog.
 *
 * This component uses a dropzone for file uploads, allowing JSON or CSV files. It validates the file content and
 * manages the upload and processing of the file using mutations. It provides feedback through notifications on
 * success, error, or cancellation actions.
 */

export function ActualDecisionsDialog(props: {
  onClose: () => void
  setIsFilePending: Dispatch<React.SetStateAction<boolean>>
}): ReactElement {
  const { onClose, setIsFilePending } = props
  const { t } = useTranslation("dialogs")

  const { id: projectId } = useParams<{ id: string }>()

  const abortControllerRef = useRef<AbortController>()

  const [acceptedFiles, setAcceptedFiles] = useState<File[]>([])

  const uploadDataFileMutation = useMutation<DataFileGroup, AxiosError<StandardErrorType>, File>(
    (file: File) => {
      abortControllerRef.current = new AbortController()
      return KonanAPI.uploadDataFile({
        project_uuid: projectId as string,
        file: file,
        signal: abortControllerRef.current.signal,
        type: DataFileGroup.type.ACTUAL_DECISIONS,
      })
    },
    {
      onSuccess({ uuid }) {
        createActualDecisionsWithFileMutation.mutate({
          project_uuid: projectId as string,
          dataFile_uuid: uuid,
        })
      },
      onError: withErrorHandler(() => {
        setAcceptedFiles([])
      }),
    },
  )

  const createActualDecisionsWithFileMutation = useMutation<
    AxiosResponse<ActualDecisionCreationJob>,
    AxiosError<StandardErrorType>,
    {
      project_uuid: string
      dataFile_uuid: string
    }
  >(KonanAPI.createActualDecisionsWithDataFile, {
    onSuccess: () => {
      setIsFilePending(true)
      onClose()
      NotificationUtils.toast(t("ActualDecisionsDialog.successMessages.uploadSuccess"), { severity: "info" })
    },
    onError: withErrorHandler(() => {
      setAcceptedFiles([])
    }),
  })

  const handleSettingSelectedFiles = async (file: File[]): Promise<void> => {
    if (file[0]?.name.endsWith(".json")) {
      try {
        const fileContent = await readFileContentAsync(file[0], 10)
        const parsedJson: Record<PropertyKey, unknown>[] = JSON.parse(fileContent)
        const isJsonKeysValid = parsedJson.every(
          (item) => item?.prediction_uuid && (item?.label_uuid || item?.label_name),
        )

        if (!isJsonKeysValid) {
          NotificationUtils.toast(t("ActualDecisionsDialog.errorMessages.jsonKeysInvalid"), {
            severity: "error",
          })
          return
        }

        setAcceptedFiles(file)
      } catch (err) {
        NotificationUtils.toast(
          err instanceof Error
            ? err.message
            : typeof err === "string"
              ? err
              : t("ActualDecisionsDialog.errorMessages.genericError"),
          { severity: "error" },
        )
      }
    }

    if (file[0]?.name.endsWith("csv")) {
      const fileHeaderContent = (await readFileContentAsync(file[0], 10)).split("\n")[0]

      const isCsvValid =
        fileHeaderContent?.includes("prediction_uuid,") &&
        (fileHeaderContent.includes("label_uuid,") || fileHeaderContent.includes("label_name,"))

      if (!isCsvValid) {
        NotificationUtils.toast(t("ActualDecisionsDialog.errorMessages.csvColumnsInvalid"), {
          severity: "error",
        })
      }
      setAcceptedFiles(file)
    }
  }

  return (
    <BaseSimpleDialog
      open
      name={""}
      onAccept={() => acceptedFiles[0] && uploadDataFileMutation.mutate(acceptedFiles[0])}
      onClose={onClose}
      mode={"actual-decision"}
      isLoading={uploadDataFileMutation.isLoading || createActualDecisionsWithFileMutation.isLoading}
      disableMainBtn={
        acceptedFiles.length === 0 ||
        uploadDataFileMutation.isLoading ||
        createActualDecisionsWithFileMutation.isLoading
      }
    >
      <Dropzone
        selectedFiles={acceptedFiles}
        uploadProgress={acceptedFiles.length === 0 ? 0 : 100}
        setSelectedFiles={handleSettingSelectedFiles}
        description={t("ActualDecisionsDialog.dropzoneTitle")}
        extensions={{ "application/json": [".json"], "text/*": [".csv"] }}
      />
    </BaseSimpleDialog>
  )
}
